import { useState, type FormEvent } from "react";
import { ChevronDown, Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MoneyInput, parseMoney } from "@/components/MoneyInput";
import { DEBT_BLOCK, FREQUENCIES, SECTIONS, TYPE_OPTS, isDebtName, periodOf, typeLabel, type Frequency, type Section } from "@/lib/budget";
import type { Category, CategoryGroup } from "@/lib/finance";
import { draftAmount, type BudgetCtl, type Draft } from "@/lib/useBudgetLines";
import { monthLabel, money, shiftMonth } from "@/lib/format";

const TIPO = TYPE_OPTS.map((t) => ({ value: t.value as string, label: t.label }));
const slug = (s: string) => s.toLowerCase().replace(/\s+/g, "-");
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const cols = (expense: boolean) => (expense ? "sm:grid-cols-[minmax(0,1.2fr)_minmax(0,1.3fr)_130px_140px_40px]" : "sm:grid-cols-[minmax(0,1.3fr)_minmax(0,1.3fr)_150px_40px]");

function MiniSelect({ value, onChange, options, testId, placeholder }: { value: string; onChange: (v: string) => void; options: { value: string; label: string }[]; testId: string; placeholder?: string }) {
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger className="h-10 text-sm" data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent className="max-h-72 bg-white">
        {options.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`${testId}-option-${o.value}`}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function nextPay(d: Draft, month: string): string {
  if (!d.payMonth || periodOf(d.frequency) <= 1) return month;
  let m = d.payMonth;
  const p = periodOf(d.frequency);
  while (m < month) m = shiftMonth(m, p);
  return m;
}

function FrequencyRow({ d, ctl, mobileCategory }: { d: Draft; ctl: BudgetCtl; mobileCategory?: React.ReactNode }) {
  const freqOpts = FREQUENCIES.map((f) => ({ value: f.value, label: f.label }));
  const next = nextPay(d, ctl.month);
  const months = Array.from({ length: periodOf(d.frequency) }, (_, i) => shiftMonth(ctl.month, i)).map((m) => ({ value: m, label: `Próximo pago: ${monthLabel(m)}` }));
  const setFreq = (v: string) => ctl.update(d.key, { frequency: v as Frequency, payMonth: d.payMonth || ctl.month });
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2" data-testid={`line-frequency-row-${d.key}`}>
      {mobileCategory && <div className="min-w-0 flex-1 sm:hidden">{mobileCategory}</div>}
      <div className="w-32 sm:w-36"><MiniSelect value={d.frequency} onChange={setFreq} options={freqOpts} testId={`line-frequency-${d.key}`} /></div>
      {d.frequency === "semimonthly" && <span className="text-xs text-slate-500" data-testid={`line-semi-note-${d.key}`}>Dos pagos al mes: cuenta {money((parseMoney(d.amountText) || 0) * 2)} al mes</span>}
      {periodOf(d.frequency) > 1 && (
        <>
          <div className="w-full sm:w-56"><MiniSelect value={months.some((o) => o.value === next) ? next : ctl.month} onChange={(v) => ctl.update(d.key, { payMonth: v })} options={months} testId={`line-paymonth-${d.key}`} /></div>
          {<span className="text-xs text-slate-500" data-testid={`line-zero-note-${d.key}`}>Cuenta {money((parseMoney(d.amountText) || 0) / periodOf(d.frequency))} al mes; lo no gastado se acumula hasta el pago</span>}
        </>
      )}
    </div>
  );
}

export type Block = Section | "debt";
const debtCat = (ctl: BudgetCtl) => ctl.categories.find((c) => isDebtName(c.name));

function TipoToggle({ d, ctl }: { d: Draft; ctl: BudgetCtl }) {
  return (
    <div className="grid h-10 grid-cols-3 rounded-md border border-[#DDE1E8] p-0.5 sm:hidden" data-testid={`line-group-m-${d.key}`}>
      {TYPE_OPTS.map(({ value: g, label, tag }) => (
        <button key={g} type="button" onClick={() => ctl.update(d.key, { group: g })} aria-label={label} data-testid={`line-group-m-${d.key}-${g}`}
          className={`rounded text-sm font-bold transition-colors ${d.group === g ? (g === "debt" ? "bg-[#9C3D24] text-white" : "bg-[#1B2A4A] text-white") : "text-slate-500"}`}>{tag}</button>
      ))}
    </div>
  );
}

function EditRow({ d, ctl, debt }: { d: Draft; ctl: BudgetCtl; debt: boolean }) {
  const cats = ctl.categories.filter((c) => c.type === SECTIONS[d.kind].catType && !isDebtName(c.name) && (!c.is_archived || c.id === d.category_id)).map((c) => ({ value: c.id, label: c.name }));
  const expense = d.kind === "expense";
  const fixed = d.kind === "income" || debt;
  const catSelect = (testId: string) => <MiniSelect value={d.category_id} onChange={(v) => ctl.update(d.key, { category_id: v })} options={cats} testId={testId} placeholder="Categoría" />;
  return (
    <li className="rounded-xl border border-[#DDE1E8] bg-white p-2 sm:p-3" data-testid={`budget-line-${d.key}`}>
      <div className={`grid items-center gap-1.5 sm:gap-2 ${expense ? "grid-cols-[66px_minmax(0,1fr)_24px]" : "grid-cols-[minmax(0,1fr)_minmax(0,110px)_24px]"} ${cols(expense)}`}>
        <Input value={d.name} maxLength={60} onChange={(e: React.ChangeEvent<HTMLInputElement>) => ctl.update(d.key, { name: e.target.value })} className={`h-10 px-2 font-semibold text-[#1B2A4A] sm:px-3 ${expense ? "col-span-3 sm:col-span-1" : ""}`} data-testid={`line-name-${d.key}`} />
        <div className="hidden min-w-0 sm:block">
          {fixed ? <p className="flex h-10 items-center px-1 text-sm text-slate-500" data-testid={`line-category-${d.key}`}>{debt ? "Deudas y obligaciones" : "Ingresos"}</p> : catSelect(`line-category-${d.key}`)}
        </div>
        {expense && (d.loanId
          ? <p className="flex h-10 items-center rounded-md bg-[#F6E3DC] px-2 text-xs font-semibold text-[#9C3D24]" title="Ligado a un crédito: siempre es Deuda" data-testid={`line-group-locked-${d.key}`}>Deuda · crédito</p>
          : <div><TipoToggle d={d} ctl={ctl} /><div className="hidden sm:block"><MiniSelect value={d.group} onChange={(v) => ctl.update(d.key, { group: v as CategoryGroup })} options={TIPO} testId={`line-group-${d.key}`} /></div></div>)}
        <div className="min-w-0"><MoneyInput id={`amt-${d.key}`} value={d.amountText} onChange={(v) => ctl.update(d.key, { amountText: v })} className="h-10" testId={`line-amount-${d.key}`} /></div>
        <button type="button" onClick={() => ctl.remove(d.key)} aria-label="Quitar" className="flex h-10 w-6 items-center justify-center rounded-md sm:w-10 text-slate-400 transition-colors hover:bg-[#F8ECE8] hover:text-[#B23A1E] sm:w-10" data-testid={`line-remove-${d.key}`}><Trash2 className="h-4 w-4" /></button>
      </div>
      <FrequencyRow d={d} ctl={ctl} mobileCategory={fixed ? undefined : catSelect(`line-category-m-${d.key}`)} />
    </li>
  );
}

function ViewRow({ d, categories }: { d: Draft; categories: Category[]; debt: boolean }) {
  const expense = d.kind === "expense";
  return (
    <li className={`grid grid-cols-[1fr_auto] items-center gap-x-3 gap-y-0.5 px-3 py-3 ${cols(expense)}`} data-testid={`budget-view-line-${d.key}`}>
      <span className="truncate font-semibold text-[#1B2A4A]">{d.name}</span>
      <span className="tabular row-start-1 text-right font-semibold text-[#1B2A4A] sm:order-4 sm:row-auto" data-testid={`view-amount-${d.key}`}>{money(parseMoney(d.amountText) || 0)}</span>
      <span className="truncate text-sm text-slate-500 sm:order-2">{categories.find((c) => c.id === d.category_id)?.name ?? "—"}{expense && <span className="sm:hidden"> · {typeLabel(d.group)}</span>}</span>
      {expense && <span className={`hidden text-sm sm:order-3 sm:block ${d.group === "debt" ? "text-[#9C3D24]" : "text-slate-500"}`} data-testid={`view-type-${d.key}`}>{typeLabel(d.group)}</span>}
      <span className="hidden sm:order-5 sm:block" />
    </li>
  );
}

function Chip({ label, onClick, testId }: { label: string; onClick: () => void; testId: string }) {
  return <button type="button" onClick={onClick} className="rounded-full border border-dashed border-[#C9A84C] px-3 py-1 text-xs font-medium text-[#1B2A4A] transition-colors hover:bg-amber-50" data-testid={testId}>+ {label}</button>;
}


function DebtOptions({ ctl, taken }: { ctl: BudgetCtl; taken: Set<string> }) {
  const c = debtCat(ctl);
  if (!c) return null;
  const opts = (c.description ?? "").split(",").map((s) => cap(s.trim())).filter((s) => s && !taken.has(s.toLowerCase()));
  return (
    <div className="mt-3 flex flex-wrap gap-2" data-testid="suggestions-debt">
      {opts.map((o) => <Chip key={o} label={o} onClick={() => ctl.add(o, "expense", c.id)} testId={`suggestion-debt-${slug(o)}`} />)}
    </div>
  );
}

function AddLine({ section, ctl, taken }: { section: Block; ctl: BudgetCtl; taken: Set<string> }) {
  const debt = section === "debt";
  const cfg = debt ? { ...DEBT_BLOCK, suggestions: [] as string[] } : SECTIONS[section];
  const [name, setName] = useState("");
  const addOne = (n: string) => (debt ? ctl.add(n, "expense", debtCat(ctl)?.id) : ctl.add(n, section));
  const submit = (e: FormEvent) => { e.preventDefault(); if (name.trim()) { addOne(name); setName(""); } };
  return (
    <div className="mt-4">
      <form onSubmit={submit} className="flex gap-2">
        <Input value={name} maxLength={60} placeholder={cfg.placeholder} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)} className="h-10 bg-white" data-testid={`add-line-input-${section}`} />
        <button type="submit" disabled={!name.trim()} className="flex h-10 shrink-0 items-center gap-1.5 rounded-md bg-[#1B2A4A] px-4 text-sm font-semibold text-white transition-colors hover:bg-[#2A3D63] disabled:opacity-40" data-testid={`add-line-button-${section}`}><Plus className="h-4 w-4" /> Agregar</button>
      </form>
      {debt ? <DebtOptions ctl={ctl} taken={taken} /> : (
        <div className="mt-3 flex flex-wrap gap-2" data-testid={`suggestions-${section}`}>
          {cfg.suggestions.filter((s) => !taken.has(s.toLowerCase())).map((s) => <Chip key={s} label={s} onClick={() => addOne(s)} testId={`suggestion-${section}-${slug(s)}`} />)}
        </div>
      )}
    </div>
  );
}

function CategoryGroup({ c, rows, ctl }: { c: Category; rows: Draft[]; ctl: BudgetCtl }) {
  const [open, setOpen] = useState(rows.length > 0);
  const [name, setName] = useState("");
  const taken = new Set(rows.map((d) => d.name.trim().toLowerCase()));
  const opts = (c.description ?? "").split(",").map((s) => cap(s.trim())).filter((s) => s && !taken.has(s.toLowerCase()));
  const total = rows.reduce((s, d) => s + draftAmount(d, ctl.month), 0);
  const submit = (e: FormEvent) => { e.preventDefault(); if (name.trim()) { ctl.add(name, "expense", c.id); setName(""); } };
  return (
    <div className="rounded-xl border border-[#DDE1E8] bg-white" data-testid={`expense-cat-${c.id}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left" data-testid={`expense-cat-toggle-${c.id}`}>
        <span className="flex items-center gap-2 font-semibold text-[#1B2A4A]">
          <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "" : "-rotate-90"}`} />{c.name}
          {rows.length > 0 && <span className="text-xs font-normal text-slate-400">{rows.length} {rows.length === 1 ? "concepto" : "conceptos"}</span>}
        </span>
        {rows.length > 0 || open ? <span className="tabular text-sm font-semibold text-[#1B2A4A]" data-testid={`expense-cat-total-${c.id}`}>{money(total)}</span>
          : <span className="flex items-center gap-1 text-sm font-semibold text-[#8A6A1F]"><Plus className="h-4 w-4" /> Agregar</span>}
      </button>
      {open && (
        <div className="space-y-3 border-t border-[#DDE1E8] bg-[#FAFAFB] p-1.5 sm:p-3">
          {rows.length > 0 && <ul className="space-y-2.5">{rows.map((d) => <EditRow key={d.key} d={d} ctl={ctl} debt={false} />)}</ul>}
          <form onSubmit={submit} className="flex gap-2">
            <Input value={name} maxLength={60} placeholder={`Agregar a ${c.name}`} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)} className="h-10 bg-white" data-testid={`add-line-input-${c.id}`} />
            <button type="submit" disabled={!name.trim()} className="flex h-10 shrink-0 items-center gap-1.5 rounded-md bg-[#1B2A4A] px-4 text-sm font-semibold text-white transition-colors hover:bg-[#2A3D63] disabled:opacity-40" data-testid={`add-line-button-${c.id}`}><Plus className="h-4 w-4" /> Agregar</button>
          </form>
          {opts.length > 0 && <div className="flex flex-wrap gap-1.5">{opts.map((o) => <Chip key={o} label={o} onClick={() => ctl.add(o, "expense", c.id)} testId={`option-${c.id}-${slug(o)}`} />)}</div>}
        </div>
      )}
    </div>
  );
}

function ExpenseByCategory({ ctl, rows }: { ctl: BudgetCtl; rows: Draft[] }) {
  const cats = ctl.categories.filter((c) => c.type === "expense" && !c.is_archived && !isDebtName(c.name));
  const ids = new Set(cats.map((c) => c.id));
  const loose = rows.filter((d) => !ids.has(d.category_id));
  return (
    <div className="space-y-2.5" data-testid="expense-by-category">
      {loose.length > 0 && (
        <div className="rounded-xl border border-dashed border-[#C9A84C] bg-white p-3" data-testid="expense-cat-none">
          <p className="mb-2 text-sm font-semibold text-[#1B2A4A]">Sin categoría: elige una para cada concepto</p>
          <ul className="space-y-2.5">{loose.map((d) => <EditRow key={d.key} d={d} ctl={ctl} debt={false} />)}</ul>
        </div>
      )}
      {cats.map((c) => <CategoryGroup key={c.id} c={c} rows={rows.filter((d) => d.category_id === c.id)} ctl={ctl} />)}
    </div>
  );
}

export function EditorBlock({ section, ctl }: { section: Block; ctl: BudgetCtl }) {
  const debt = section === "debt";
  const cfg = debt ? DEBT_BLOCK : SECTIONS[section];
  const debtId = debtCat(ctl)?.id;
  const rows = (ctl.draft ?? []).filter((d) => (debt ? d.kind === "expense" && d.category_id === debtId
    : d.kind === section && !(section === "expense" && d.category_id === debtId)));
  const total = rows.reduce((s, d) => s + draftAmount(d, ctl.month), 0);
  const taken = new Set(rows.map((d) => d.name.trim().toLowerCase()));
  const expense = section === "expense" || debt;
  return (
    <div className="rounded-[14px] border border-[#DDE1E8] bg-[#FAFAFB] p-3 sm:p-6" data-testid={`editor-block-${section}`}>
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <p className="font-display text-lg font-bold text-[#1B2A4A]">{cfg.title}</p>
          <p className="mt-0.5 text-sm text-slate-500">{cfg.hint}</p>
        </div>
        <p className="tabular shrink-0 text-right font-display text-xl font-bold text-[#1B2A4A]" data-testid={`editor-total-${section}`}>{money(total)}<span className="block text-xs font-normal text-slate-400">en {monthLabel(ctl.month)}</span></p>
      </div>
      {!ctl.draft ? <div className="h-24 animate-pulse rounded-lg bg-white" /> : section === "expense" && ctl.editing ? <ExpenseByCategory ctl={ctl} rows={rows} /> : (
        <>
          {rows.length > 0 && (
            <div className={`mb-2 hidden gap-2 px-3 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400 sm:grid ${cols(expense)}`}>
              <span>{expense ? "Gasto" : "Concepto"}</span><span>Categoría</span>{expense && <span>Tipo</span>}<span className={ctl.editing ? "" : "text-right"}>Presupuestado</span><span />
            </div>
          )}
          <ul className={ctl.editing ? "space-y-2.5" : "divide-y divide-[#DDE1E8] rounded-xl border border-[#DDE1E8] bg-white"}>
            {rows.map((d) => (ctl.editing ? <EditRow key={d.key} d={d} ctl={ctl} debt={debt} /> : <ViewRow key={d.key} d={d} categories={ctl.categories} debt={debt} />))}
            {rows.length === 0 && <li className="rounded-xl border border-dashed border-[#DDE1E8] p-5 text-center text-sm text-slate-500" data-testid={`editor-empty-${section}`}>{ctl.editing ? "Escribe una línea o toca una opción." : "Sin líneas."}</li>}
          </ul>
        </>
      )}
      {ctl.editing && section !== "expense" && <AddLine section={section} ctl={ctl} taken={taken} />}
    </div>
  );
}
